import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, FileText, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { DateOnly } from "@/lib/date-only"
import { cn } from "@/lib/utils"
import { ChLegalGuardianAttestationDrawer } from "@/modules/patients/detail/legal-guardian-attestation/ch/ch-legal-guardian-attestation-drawer"
import {
  chLegalGuardianAttestationFragment$data,
  chLegalGuardianAttestationFragment$key,
} from "@/modules/patients/detail/legal-guardian-attestation/ch/services/__generated__/chLegalGuardianAttestationFragment.graphql"
import { chLegalGuardianAttestationFragment } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/chLegalGuardianAttestationFragment"
import { DeLegalGuardianAttestationDrawer } from "@/modules/patients/detail/legal-guardian-attestation/de/de-legal-guardian-attestation-drawer"
import {
  deLegalGuardianAttestationFragment$data,
  deLegalGuardianAttestationFragment$key,
} from "@/modules/patients/detail/legal-guardian-attestation/de/services/__generated__/deLegalGuardianAttestationFragment.graphql"
import { deLegalGuardianAttestationFragment } from "@/modules/patients/detail/legal-guardian-attestation/de/services/deLegalGuardianAttestationFragment"
import { useLegalGuardianAttestationDrawerContext } from "@/modules/patients/detail/legal-guardian-attestation/store/useLegalGuardianAttestationDrawerStore"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface LegalGuardianAttestationProps extends DocumentProps {
  attestationGuardianRef: chLegalGuardianAttestationFragment$key | deLegalGuardianAttestationFragment$key
  isEditable?: boolean
}

const LegalGuardianAttestation = ({
  attestationGuardianRef,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: LegalGuardianAttestationProps) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()

  const legalGuardianAttestation: chLegalGuardianAttestationFragment$data | deLegalGuardianAttestationFragment$data =
    useFragment(
      countryCode === "DE" ? deLegalGuardianAttestationFragment : chLegalGuardianAttestationFragment,
      attestationGuardianRef,
    )

  const { id, progressNoteId, firstName, lastName, durationFrom, durationTo } = legalGuardianAttestation ?? {}
  const setLegalGuardianAttestation = useLegalGuardianAttestationDrawerContext(
    (state) => state.setLegalGuardianAttestation,
  )
  const setProgressNoteId = useLegalGuardianAttestationDrawerContext((state) => state.setProgressNoteId)
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  if (!id) return null

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="legal-guardian-attestation-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }

        openDrawer(
          () => (countryCode === "DE" ? <DeLegalGuardianAttestationDrawer /> : <ChLegalGuardianAttestationDrawer />),
          "LegalGuardianAttestationDrawer",
        )
        setProgressNoteId(progressNoteId)
        setLegalGuardianAttestation(id, attestationGuardianRef)
      }}>
      <FileText className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className={cn("ml-2 grid min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {t("episodeOfCare.progressNote.attestationForLegalGuardian")}
        </ListText>
        <ListText
          className={cn("truncate", isSelected && "text-on-primary-surface")}
          variant="secondary">{`${new DateOnly(durationFrom).toDisplayableString()} - ${new DateOnly(
          durationTo,
        ).toDisplayableString()} • ${t("episodeOfCare.progressNote.for")} ${firstName} ${lastName}`}</ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="legal-guardian-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="legal-guardian-view-document-button" />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}
export { LegalGuardianAttestation }
